import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../config/firebase';

// Module-level flag — persists across component remounts and navigation.
// Resets only when the module is reloaded (full page refresh) or on sign-out.
// useRef resets on every component remount, causing the refresh to run
// multiple times per session when components mount/unmount during navigation.
let sessionClaimsRefreshed = false;

// Returns the user's roles as an array, handling both old (role string)
// and new (roles array) document formats for backwards compatibility.
export function getUserRoles(userDoc) {
  if (!userDoc) return [];
  if (Array.isArray(userDoc.roles)) return userDoc.roles;
  if (userDoc.role) return [userDoc.role];
  return [];
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // authReady becomes true only after user document is loaded, claims are
  // refreshed, and the 1500ms token propagation delay has fully completed.
  // Components must gate Firestore queries on authReady, not user?.uid.
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userRef = doc(db, 'users', firebaseUser.uid);
          const snap = await getDoc(userRef);
          if (snap.exists()) {
            const data = snap.data();

            // Auto-migrate: if document has role string but no roles array,
            // write roles array silently — handles existing documents
            if (data.role && !data.roles) {
              try {
                await updateDoc(userRef, { roles: [data.role] });
                data.roles = [data.role];
              } catch {
                // Non-fatal — continue with the role string in memory
                data.roles = [data.role];
              }
            }

            // Refresh custom claims ONCE per browser session only.
            // Using a module-level variable (not useRef) so the flag persists
            // across component remounts during navigation — useRef resets to
            // false on every remount, causing repeated refreshes per page load.
            if (!sessionClaimsRefreshed) {
              sessionClaimsRefreshed = true;
              try {
                const fns = getFunctions();
                const refreshClaims = httpsCallable(fns, 'refreshUserClaims');
                await refreshClaims();
                // Force a single token refresh so new claims are active
                await firebaseUser.getIdToken(true);
                // Wait for the Firebase SDK to propagate the new token
                // to the Firestore client before any queries run.
                // 1500ms is more reliable across varying network conditions.
                await new Promise(resolve => setTimeout(resolve, 1500));
                console.log('Claims refreshed once for this session');
              } catch (err) {
                // Non-fatal — log and continue
                console.warn('Claims refresh failed, continuing:', err.message);
              }
            }

            setUserProfile(data);
          } else {
            // No /users/ document — check if this is a company portal user
            // whose profile lives in custom claims (not in /users/).
            try {
              // Force a token refresh (true) rather than reading the cached
              // token. A company account has no /users document, so it can
              // never reach the forced refresh in the branch above; a
              // returning or restored session would otherwise be judged on a
              // token minted before inviteCompanyToScreening set its claims,
              // yielding roles=[] and a null profile. CompanyLoginPage already
              // forces a refresh, so this only costs a round-trip on the paths
              // that bypass the login page — exactly the ones that were broken.
              const tokenResult = await firebaseUser.getIdTokenResult(true);
              const claims = tokenResult.claims;
              const claimRoles = Array.isArray(claims.roles) ? claims.roles :
                (claims.role ? [claims.role] : []);
              if (claimRoles.length > 0) {
                setUserProfile({
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  roles: claimRoles,
                  role: claimRoles[0],
                  deal_id: claims.deal_id || null,
                  fund_id: claims.fund_id || null,
                  _fromClaims: true,
                });
              } else {
                setUserProfile(null);
              }
            } catch {
              setUserProfile(null);
            }
          }
        } catch {
          setUserProfile(null);
        }

        // authReady is set AFTER all of the above — user doc loaded, claims
        // refreshed, token propagation delay complete. Firestore queries are
        // safe to run from this point forward.
        // This is inside if (firebaseUser) so it never fires on sign-out.
        setAuthReady(true);
      } else {
        // User signed out — reset module-level flag so next login gets a
        // fresh claims refresh
        sessionClaimsRefreshed = false;
        setUser(null);
        setUserProfile(null);
        setAuthReady(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const roles = getUserRoles(userProfile);

  return {
    user,
    userProfile,
    loading,
    authReady,
    isAdmin: roles.includes('admin'),
    roles,
  };
}
